import { assert, lazy } from '@deqo/foundation';
import { Component } from './Component';
import { Name, Owner } from './FieldSymbols';
import { FieldDefinition, FieldDescriptor, HasProperties } from './Interfaces';
import { Manifest } from './Manifest';
import * as ObjectSymbols from './ObjectSymbols';

export function field(def: FieldDefinition): PropertyDecorator {
    return ({ constructor }: any, name: string | symbol) => {
        // Prevent use on symbol properties
        if (typeof (name) !== 'string') throw new Error('field failure: Cannot use symbol as property key.');
        // Create descriptor
        const fd = Object.assign({ name }, def) as any;
        // Register on type
        Manifest.registerField(constructor, fd);
        // Return property override
        return {
            configurable: false,
            get(this: Component) {
                const fi = this[ObjectSymbols.Fields][fd.name];
                return fd.valueOnly ? (fi as any).value : fi;
            },
            set(this: Component, value: any) {
                const fi = this[ObjectSymbols.Fields][fd.name];
                assert(fd.valueOnly, 'field failure: Cannot set non-value field property.');
                (fi as any).value = value;
            }
        };
    };
}

export abstract class Field implements HasProperties {

    public static describe<T extends Field>(instance: T): FieldDescriptor<T> {
        const cd = Component.describe(instance.owner);
        const fd = Manifest.describeField(cd.type, instance.name);
        return fd as FieldDescriptor<T>;
    }

    public [Name]: string;
    public [Owner]: Component;

    public abstract get type(): string;

    @lazy()
    public get id() {
        return `${this.owner.id}/${this.name}`;
    }

    public get name() {
        return this[Name];
    }

    public get owner() {
        return this[Owner];
    }

    public get(propertyName: string) {
        return this[Owner].get(this.qualify(propertyName));
    }

    public set(propertyName: string, value: any): void {
        this[Owner].set(this.qualify(propertyName), value);
    }

    public toString() {
        return this.formatString();
    }

    protected abstract formatString(): string;

    private qualify(propertyName: string) {
        return `${this[Name]}|${propertyName}`;
    }
}
